import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Send, CheckCircle, MapPin, Clock, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContractorRespondPage() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [job, setJob] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadJob();
  }, [jobId, token]);

  const loadJob = async () => {
    if (!token) {
      setError('Invalid or missing token');
      setLoading(false);
      return;
    }

    try {
      // Verify token and get job details
      const res = await axios.get(`${API}/api/contractor-portal/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJob(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('This link has expired. Please contact the helpdesk for a new link.');
      } else {
        setError('Failed to load job details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/api/contractor-portal/job/${jobId}/respond`, {
        message: message,
        token: token
      });
      setSent(true);
      toast.success('Response sent successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send response');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#007073]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Unable to Load</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Response Sent</h2>
            <p className="text-slate-500 mb-4">Thank you! The helpdesk has received your message.</p>
            <Badge variant="outline" className="text-[#007073]">
              {job?.job_reference || job?.job_number || jobId}
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/phs-logo.png" 
            alt="Park Home Support" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-slate-900">Respond to Job</h1>
        </div>

        {/* Job Summary Card */}
        {job && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Badge className="bg-[#007073]">
                  {job.job_reference || job.job_number}
                </Badge>
                <Badge variant="outline">{job.status}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="h-4 w-4" />
                  <span>{job.site_name || job.park || job.property_address}</span>
                </div>
                {job.plot && (
                  <p className="text-slate-500 ml-6">Plot {job.plot}</p>
                )}
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="h-4 w-4" />
                  <span>{job.description || job.issue || 'See job details'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Response Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Response</CardTitle>
            <CardDescription>
              Send a message to the helpdesk about this job
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here... e.g. 'I'll be on site tomorrow at 10am' or 'Please provide access details'"
              className="min-h-[120px] mb-4"
            />
            <Button 
              onClick={handleSend} 
              disabled={sending || !message.trim()}
              className="w-full bg-[#007073] hover:bg-[#005456]"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Response
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Leisure Home Solutions Ltd
        </p>
      </div>
    </div>
  );
}
